import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, type MessagingJobSummary } from "@/lib/api";
import { cn } from "@/lib/utils";
import { LogViewer } from "@/components/LogViewer";

const STATUS_STYLE: Record<string, string> = {
  running: "bg-moving-100 text-moving-700",
  queued: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  stopped_errors: "bg-red-100 text-red-700",
};

export function MessagingJobMonitor() {
  const [selected, setSelected] = useState<string | null>(null);
  const jobs = useQuery({
    queryKey: ["messaging-jobs"],
    queryFn: () => api.messagingJobs(),
    refetchInterval: 5000,
  });

  const list: MessagingJobSummary[] = jobs.data?.jobs ?? [];
  const activeJob = selected ?? (list.find((j) => j.status === "running")?.job_id ?? null);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Lista job */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-moving-600" />
          <h3 className="font-semibold text-slate-900">Job</h3>
          {jobs.isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
          ) : null}
        </div>
        {list.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun job ancora lanciato.</p>
        ) : (
          <div className="space-y-1.5">
            {list.map((j) => {
              const isSel = activeJob === j.job_id;
              return (
                <button
                  key={j.job_id}
                  type="button"
                  onClick={() => setSelected(j.job_id)}
                  className={cn(
                    "w-full rounded-lg border p-2.5 text-left text-sm transition",
                    isSel
                      ? "border-moving-500 bg-moving-50"
                      : "border-slate-100 hover:border-slate-300",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium text-slate-800">
                      {j.label || j.job_id}
                    </span>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
                        STATUS_STYLE[j.status] ?? "bg-slate-100 text-slate-600",
                      )}
                    >
                      {j.status}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    <span>{j.channel}</span>
                    {j.total != null ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        {j.sent ?? 0}/{j.total}
                      </span>
                    ) : null}
                    {j.failed ? (
                      <span className="inline-flex items-center gap-1 text-red-500">
                        <XCircle className="h-3 w-3" />
                        {j.failed}
                      </span>
                    ) : null}
                  </div>
                  {j.total ? (
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-1.5 rounded-full bg-moving-500"
                        style={{
                          width: `${Math.min(100, Math.round(((j.sent ?? 0) / j.total) * 100))}%`,
                        }}
                      />
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Log live del job selezionato */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Log live</h3>
        {activeJob ? (
          <LogViewer slug={`messaging-${activeJob}`} height="320px" />
        ) : (
          <p className="text-sm text-slate-500">
            Seleziona un job per vedere il log in tempo reale.
          </p>
        )}
      </div>
    </div>
  );
}
